import { useState, useEffect } from 'react';
import { AdminLayout } from '../components/AdminLayout';
import { useAuth } from '../context/AuthContext';
import { 
  ScrollText,
  Loader2,
  Search,
  Filter,
  AlertTriangle,
  User,
  Shield,
  Ban,
  CheckCircle,
  Clock,
  ChevronLeft,
  ChevronRight
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { toast } from '../lib/toast';
import axios from 'axios';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const EVENT_TYPE_CONFIG = {
  'user.created': { label: 'User Created', color: 'bg-green-100 text-green-700', icon: User },
  'user.updated': { label: 'User Updated', color: 'bg-blue-100 text-blue-700', icon: User },
  'user.banned': { label: 'User Banned', color: 'bg-red-100 text-red-700', icon: Ban },
  'user.unbanned': { label: 'User Unbanned', color: 'bg-green-100 text-green-700', icon: CheckCircle },
  'user.role_changed': { label: 'Role Changed', color: 'bg-purple-100 text-purple-700', icon: Shield },
  'user.verified': { label: 'Email Verified', color: 'bg-blue-100 text-blue-700', icon: CheckCircle },
  'user.premium_changed': { label: 'Premium Changed', color: 'bg-amber-100 text-amber-700', icon: User },
  'user.deleted': { label: 'User Deleted', color: 'bg-red-100 text-red-700', icon: User },
  'listing.hidden': { label: 'Listing Hidden', color: 'bg-gray-100 text-gray-700', icon: AlertTriangle },
  'listing.restored': { label: 'Listing Restored', color: 'bg-green-100 text-green-700', icon: CheckCircle },
  'listing.deleted': { label: 'Listing Deleted', color: 'bg-red-100 text-red-700', icon: AlertTriangle },
  'report.created': { label: 'Report Created', color: 'bg-orange-100 text-orange-700', icon: AlertTriangle },
  'report.reviewed': { label: 'Report Reviewed', color: 'bg-blue-100 text-blue-700', icon: CheckCircle },
  'report.resolved': { label: 'Report Resolved', color: 'bg-green-100 text-green-700', icon: CheckCircle },
  'admin.login': { label: 'Admin Login', color: 'bg-purple-100 text-purple-700', icon: Shield },
  'admin.action': { label: 'Admin Action', color: 'bg-purple-100 text-purple-700', icon: Shield }
};

const formatDate = (isoString) => {
  if (!isoString) return '-';
  const date = new Date(isoString);
  const day = String(date.getDate()).padStart(2, '0');
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const year = date.getFullYear();
  const hours = String(date.getHours()).padStart(2, '0');
  const minutes = String(date.getMinutes()).padStart(2, '0');
  const seconds = String(date.getSeconds()).padStart(2, '0');
  return `${day}/${month}/${year} ${hours}:${minutes}:${seconds}`;
};

export default function AdminAuditLogsPage() {
  const { token } = useAuth();
  const [loading, setLoading] = useState(true);
  const [logs, setLogs] = useState([]);
  const [total, setTotal] = useState(0);
  const [enabled, setEnabled] = useState(false);
  const [eventTypes, setEventTypes] = useState([]);
  const [selectedEventType, setSelectedEventType] = useState('');
  const [page, setPage] = useState(0);
  const limit = 25;

  useEffect(() => {
    fetchEventTypes();
    fetchLogs();
  }, [token, selectedEventType, page]);

  const fetchEventTypes = async () => {
    try {
      const res = await axios.get(`${API_URL}/api/admin/audit-logs/event-types`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setEventTypes(res.data.event_types || []);
    } catch (err) {
      console.error('Failed to fetch event types');
    }
  };

  const fetchLogs = async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams({
        skip: page * limit,
        limit: limit
      });
      if (selectedEventType) {
        params.append('event_type', selectedEventType);
      }
      
      const res = await axios.get(`${API_URL}/api/admin/audit-logs?${params}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      setEnabled(res.data.enabled);
      setLogs(res.data.logs || []);
      setTotal(res.data.total || 0);
    } catch (err) {
      toast.error('Failed to load audit logs');
    } finally {
      setLoading(false);
    }
  };

  const getEventBadge = (eventType) => {
    const config = EVENT_TYPE_CONFIG[eventType] || {
      label: eventType,
      color: 'bg-gray-100 text-gray-600',
      icon: Clock
    };
    const Icon = config.icon;
    return (
      <span className={`px-2 py-1 rounded-full text-xs font-medium flex items-center gap-1 ${config.color}`}>
        <Icon className="w-3 h-3" />
        {config.label}
      </span>
    );
  };

  if (loading && logs.length === 0) {
    return (
      <AdminLayout>
        <div className="flex items-center justify-center min-h-[60vh]">
          <Loader2 className="w-8 h-8 animate-spin text-purple-600" />
        </div>
      </AdminLayout>
    );
  }

  return (
    <AdminLayout>
      <div className="max-w-5xl mx-auto">
        {/* Header */}
        <div className="flex items-center justify-between mb-8">
          <div>
            <h1 className="text-2xl font-bold text-gray-900 flex items-center gap-2">
              <ScrollText className="w-6 h-6 text-purple-600" />
              Audit Logs
            </h1>
            <p className="text-gray-500">Track admin actions and user state changes</p>
          </div>
          {!enabled && (
            <div className="px-3 py-2 rounded-lg bg-yellow-50 border border-yellow-200">
              <p className="text-sm text-yellow-700 flex items-center gap-2">
                <AlertTriangle className="w-4 h-4" />
                Audit logging is disabled
              </p>
            </div>
          )}
        </div>

        {!enabled ? (
          <div className="bg-white border border-gray-200 rounded-xl p-12 text-center">
            <AlertTriangle className="w-12 h-12 text-yellow-500 mx-auto mb-4" />
            <h3 className="text-lg font-medium text-gray-900 mb-2">Audit Logging Disabled</h3>
            <p className="text-gray-500 mb-4 max-w-md mx-auto">
              Audit logging is currently disabled. To enable it, set the environment variable:
            </p>
            <code className="bg-gray-100 px-4 py-2 rounded text-sm font-mono text-gray-800">
              AUDIT_LOG_ENABLED=true
            </code>
            <p className="text-xs text-gray-400 mt-4">
              See MIGRATION_PLAN.md for deployment instructions
            </p>
          </div>
        ) : (
          <>
            {/* Filters */}
            <div className="flex gap-4 mb-6">
              <div className="flex items-center gap-2">
                <Filter className="w-4 h-4 text-gray-400" />
                <select
                  value={selectedEventType}
                  onChange={(e) => {
                    setSelectedEventType(e.target.value);
                    setPage(0);
                  }}
                  className="border border-gray-300 rounded-lg px-3 py-2 text-sm bg-white"
                >
                  <option value="">All Events</option>
                  {eventTypes.map((type) => (
                    <option key={type} value={type}>
                      {EVENT_TYPE_CONFIG[type]?.label || type}
                    </option>
                  ))}
                </select>
              </div>
              <div className="text-sm text-gray-500 flex items-center">
                {total} total events
              </div>
            </div>

            {/* Logs Table */}
            <div className="bg-white border border-gray-200 rounded-xl overflow-hidden shadow-sm">
              <table className="w-full">
                <thead className="bg-gray-50 border-b border-gray-200">
                  <tr className="text-left text-xs text-gray-500">
                    <th className="px-4 py-3 font-medium">Timestamp</th>
                    <th className="px-4 py-3 font-medium">Event</th>
                    <th className="px-4 py-3 font-medium">Actor</th>
                    <th className="px-4 py-3 font-medium">Target</th>
                    <th className="px-4 py-3 font-medium">Details</th>
                  </tr>
                </thead>
                <tbody>
                  {logs.length === 0 ? (
                    <tr>
                      <td colSpan={5} className="px-4 py-12 text-center text-gray-500">
                        No audit logs found
                      </td>
                    </tr>
                  ) : (
                    logs.map((log) => (
                      <tr key={log.id} className="border-b border-gray-100 last:border-0 hover:bg-gray-50">
                        <td className="px-4 py-3">
                          <span className="text-xs text-gray-600 font-mono">
                            {formatDate(log.created_at)}
                          </span>
                        </td>
                        <td className="px-4 py-3">
                          {getEventBadge(log.event_type)}
                        </td>
                        <td className="px-4 py-3">
                          <div>
                            <p className="text-sm text-gray-900">{log.actor_email}</p>
                            <p className="text-xs text-gray-400">{log.actor_id?.slice(0, 8)}...</p>
                          </div>
                        </td>
                        <td className="px-4 py-3">
                          {log.target_id ? (
                            <div>
                              <p className="text-sm text-gray-600">{log.target_type}</p>
                              <p className="text-xs text-gray-400">{log.target_id?.slice(0, 8)}...</p>
                            </div>
                          ) : (
                            <span className="text-xs text-gray-400">-</span>
                          )}
                        </td>
                        <td className="px-4 py-3">
                          {log.old_value && log.new_value ? (
                            <div className="text-xs">
                              <span className="text-red-500 line-through">{log.old_value}</span>
                              <span className="mx-1">→</span>
                              <span className="text-green-600">{log.new_value}</span>
                            </div>
                          ) : log.details && Object.keys(log.details).length > 0 ? (
                            <div className="text-xs text-gray-500">
                              {Object.entries(log.details).slice(0, 2).map(([k, v]) => (
                                <div key={k}>{k}: {String(v).slice(0, 30)}</div>
                              ))}
                            </div>
                          ) : (
                            <span className="text-xs text-gray-400">-</span>
                          )}
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>

            {/* Pagination */}
            {total > limit && (
              <div className="flex items-center justify-between mt-4">
                <p className="text-sm text-gray-500">
                  Showing {page * limit + 1} - {Math.min((page + 1) * limit, total)} of {total}
                </p>
                <div className="flex gap-2">
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => setPage(p => Math.max(0, p - 1))}
                    disabled={page === 0}
                  >
                    <ChevronLeft className="w-4 h-4" />
                    Previous
                  </Button>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => setPage(p => p + 1)}
                    disabled={(page + 1) * limit >= total}
                  >
                    Next
                    <ChevronRight className="w-4 h-4" />
                  </Button>
                </div>
              </div>
            )}
          </>
        )}
      </div>
    </AdminLayout>
  );
}
